import { useId, type ReactNode } from 'react';

/**
 * The deck's inline diagrams. None is new artwork:
 *
 * - The kit and the four diagrams under it are ported from Orca 101, in
 *   orca-website/src/components/diagrams/learn/. Geometry, labels and part
 *   ids are the site's; only the Tailwind colour classes became the deck's
 *   `.k-*` classes (orca.css). README.md lists what each leaves out.
 * - `WorkflowDiagram` is lifted from the booth loop
 *   (decks/booth-decks/ai-conference-booth, frame 17).
 *
 * The website's static diagrams are not here: they are files under images/,
 * shown as <img> by `Figure` in parts.tsx.
 */

/* ── the kit ─────────────────────────────────────────────────────────────
   Lime stroke and label = Orca runs it; grey stroke and white label = you run
   it, or it is a service you call. Solid = the session waits on this call;
   dashed = appended asynchronously. Being "on" is weight and opacity, never
   colour.

   Geometry stays in SVG attributes and only colour is a class. Arrowheads are
   paths, never <marker>: a marker needs a document-global id, and every slide
   of a deck is in the DOM at once. */

type Owner = 'orca' | 'you';

const STROKE: Record<Owner, string> = { orca: 'k-stroke-orca', you: 'k-stroke-you' };
const TINT: Record<Owner, string> = { orca: 'k-tint-orca', you: 'k-tint-you' };
const LABEL: Record<Owner, string> = { orca: 'k-label-orca', you: 'k-label-you' };

/** Baseline that centres a single line of DM Mono on `cy`. */
const centred = (cy: number, size: number) => cy + size * 0.34;

/**
 * The parts a slide lights, as the set a diagram reads. Each diagram types its
 * own part ids, so a slide that names a part its diagram does not have fails
 * `tsc` instead of lighting nothing.
 */
export const lit = <P extends string>(...parts: P[]): ReadonlySet<P> => new Set(parts);

/**
 * One addressable part of a diagram. A part that is off stays visible but dim,
 * unless `quiet="hide"`: a part that only exists while it is chosen.
 */
function Part({
  id,
  on,
  quiet = 'dim',
  children,
}: {
  id: string;
  on: boolean;
  quiet?: 'dim' | 'hide';
  children: ReactNode;
}) {
  const state = on ? '' : quiet === 'hide' ? ' k-hidden' : ' k-off';
  return (
    <g data-part={id} className={`k-part${state}`}>
      {children}
    </g>
  );
}

/** An outlined box with a centred label, and an optional second line. */
function Box({
  x,
  y,
  w,
  h,
  label,
  sub,
  owner = 'orca',
  on,
  size = 14,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  sub?: string;
  owner?: Owner;
  on: boolean;
  size?: number;
}) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={4}
        strokeWidth={on ? 2 : 1.25}
        className={`${STROKE[owner]} ${on ? TINT[owner] : 'k-fill-none'}`}
      />
      <text
        x={cx}
        y={sub ? cy - 4 : centred(cy, size)}
        fontSize={size}
        textAnchor="middle"
        className={`k-text ${LABEL[owner]}`}
      >
        {label}
      </text>
      {sub && (
        <text x={cx} y={cy + 15} fontSize={13} textAnchor="middle" className="k-text k-label-muted">
          {sub}
        </text>
      )}
    </>
  );
}

type Tip = readonly [x: number, y: number, degrees: number];

function Head({ at: [x, y, degrees] }: { at: Tip }) {
  return (
    <path
      d="M0,0 L-8,-4 L-8,4 Z"
      transform={`translate(${x} ${y}) rotate(${degrees})`}
      className="k-head"
    />
  );
}

/**
 * A call or a flow: a path, and an arrowhead at `tip` as `[x, y, degrees]`
 * (0 points right, 90 down, 180 left, 270 up). `tail` is a second arrowhead
 * at the path's other end.
 */
function Arrow({
  d,
  tip,
  tail,
  on,
  dashed = false,
}: {
  d: string;
  tip?: Tip;
  tail?: Tip;
  on: boolean;
  dashed?: boolean;
}) {
  return (
    <>
      <path
        d={d}
        fill="none"
        strokeWidth={on ? 2 : 1.25}
        strokeDasharray={dashed ? '5 4' : undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="k-stroke-flow"
      />
      {tip && <Head at={tip} />}
      {tail && <Head at={tail} />}
    </>
  );
}

/** A flow label or a container title. */
function Label({
  x,
  y,
  anchor = 'middle',
  size = 13,
  tone = 'muted',
  children,
}: {
  x: number;
  y: number;
  anchor?: 'start' | 'middle' | 'end';
  size?: number;
  tone?: 'muted' | Owner;
  children: string;
}) {
  return (
    <text
      x={x}
      y={y}
      fontSize={size}
      textAnchor={anchor}
      className={`k-text ${tone === 'muted' ? 'k-label-muted' : LABEL[tone]}`}
    >
      {children}
    </text>
  );
}

/** The <svg> every ported diagram shares, sized by the figure it sits in. */
function Canvas({
  width,
  height,
  label,
  children,
}: {
  width: number;
  height: number;
  label: string;
  children: ReactNode;
}) {
  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} focusable="false">
      {children}
    </svg>
  );
}

/* ── Orca 101, guide 1 ───────────────────────────────────────────────── */

type LoopPart = 'harness' | 'message' | 'model' | 'call' | 'tool' | 'result' | 'reply';
const LOOP_ALL = lit<LoopPart>('harness', 'message', 'model', 'call', 'tool', 'result', 'reply');

/**
 * One turn of the agent loop, inside a harness. Nothing here is lime: this is
 * the picture before a runtime enters it.
 */
export function LoopDiagram({ on = LOOP_ALL }: { on?: ReadonlySet<LoopPart> }) {
  const harness = on.has('harness');
  return (
    <Canvas
      width={420}
      height={250}
      label="One turn of the agent loop: a message goes to the model, the model asks for a tool, the tool's result goes back, and a reply comes out"
    >
      <Part id="harness" on={harness}>
        <rect
          x={84}
          y={18}
          width={252}
          height={214}
          rx={10}
          strokeWidth={harness ? 2 : 1.25}
          className="k-stroke-frame k-fill-none"
        />
        <Label x={100} y={40} anchor="start" tone="you">
          Harness
        </Label>
      </Part>

      <Part id="message" on={on.has('message')}>
        <Arrow d="M8,78 H150" tip={[150, 78, 0]} on={on.has('message')} />
        <Label x={10} y={66} anchor="start">
          message
        </Label>
      </Part>

      <Part id="model" on={on.has('model')}>
        <Box x={150} y={56} w={120} h={44} label="Model" owner="you" on={on.has('model')} />
      </Part>

      <Part id="call" on={on.has('call')}>
        <Arrow d="M236,100 V164" tip={[236, 164, 90]} on={on.has('call')} />
        <Label x={246} y={137} anchor="start">
          tool call
        </Label>
      </Part>

      <Part id="tool" on={on.has('tool')}>
        <Box x={150} y={164} w={120} h={44} label="Tool" owner="you" on={on.has('tool')} />
      </Part>

      <Part id="result" on={on.has('result')}>
        <Arrow d="M184,164 V100" tip={[184, 100, 270]} on={on.has('result')} />
        <Label x={174} y={137} anchor="end">
          result
        </Label>
      </Part>

      <Part id="reply" on={on.has('reply')}>
        <Arrow d="M270,78 H412" tip={[412, 78, 0]} on={on.has('reply')} />
        <Label x={410} y={66} anchor="end">
          reply
        </Label>
      </Part>
    </Canvas>
  );
}

/** Every label on the runtime map is one size. */
const SIZE = 13;

/** The runtime map's nodes, then the calls between them. */
type RuntimePart =
  | 'app'
  | 'registry'
  | 'harness'
  | 'sandbox'
  | 'gateway'
  | 'store'
  | 'model'
  | 'mcp'
  | 'send'
  | 'stream'
  | 'append'
  | 'consume'
  | 'prepare'
  | 'run'
  | 'egress'
  | 'to-model'
  | 'to-mcp'
  | 'record';

/** The eight hops of Orca 101's "one message, hop by hop". */
type Hop = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/**
 * Where a hop number sits on the runtime map. The numbers are the one thing
 * this deck adds to the site's drawing: the site lights one hop at a time,
 * and a slide shows four at once. Hops 4 and 7 are the same call, the harness
 * asking the sandbox to run something, and are never on one slide.
 */
const HOP_AT: Record<Hop, readonly [number, number]> = {
  1: [122, 48],
  2: [168, 170],
  3: [276, 218],
  4: [340, 126],
  5: [446, 108],
  6: [504, 204],
  7: [340, 126],
  8: [122, 98],
};

/**
 * The runtime map: your application, the pieces Orca Agent Engine runs, and
 * the services they call.
 *
 * Model and MCP calls both leave through the AI Gateway. That is how the
 * site's architecture diagram draws it. The engine's own default is to call
 * the model provider directly, so do not "correct" one without the other.
 */
export function RuntimeDiagram({ on, hops = [] }: { on: ReadonlySet<RuntimePart>; hops?: readonly Hop[] }) {
  // The gradient needs an id, and every slide is in the DOM at once, so the id
  // has to be this instance's own.
  const gradient = useId();
  const harness = on.has('harness');

  return (
    <Canvas
      width={640}
      height={312}
      label="The runtime map: your app calls the Registry; inside Orca Agent Engine sit the Registry, the Harness Server, the Sandbox, the AI Gateway and the Event Store; the gateway calls the model provider and MCP servers"
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3da09d" />
          <stop offset="1" stopColor="#e2ffa5" />
        </linearGradient>
      </defs>

      {/* The Orca container: not a part, it is the frame the parts sit in. */}
      <rect
        x={124}
        y={10}
        width={380}
        height={292}
        rx={10}
        fill="none"
        strokeWidth={1.25}
        stroke={`url(#${gradient})`}
      />
      <Label x={140} y={34} anchor="start" size={SIZE} tone="orca">
        Orca Agent Engine
      </Label>

      <Part id="app" on={on.has('app')}>
        <Box x={8} y={52} w={96} h={44} label="Your app" owner="you" size={SIZE} on={on.has('app')} />
      </Part>

      <Part id="send" on={on.has('send')}>
        <Arrow d="M104,66 H140" tip={[140, 66, 0]} on={on.has('send')} />
      </Part>

      <Part id="stream" on={on.has('stream')}>
        <Arrow d="M204,244 V96" tip={[204, 96, 270]} on={on.has('stream')} dashed />
        <Arrow d="M140,82 H104" tip={[104, 82, 180]} on={on.has('stream')} dashed />
      </Part>

      <Part id="registry" on={on.has('registry')}>
        <Box x={140} y={52} w={100} h={44} label="Registry" size={SIZE} on={on.has('registry')} />
      </Part>

      <Part id="append" on={on.has('append')}>
        <Arrow d="M168,96 V244" tip={[168, 244, 90]} on={on.has('append')} />
      </Part>

      <Part id="prepare" on={on.has('prepare')}>
        <Arrow d="M260,64 H240" tip={[240, 64, 180]} on={on.has('prepare')} />
      </Part>

      <Part id="harness" on={harness}>
        <rect
          x={260}
          y={44}
          width={132}
          height={60}
          rx={4}
          strokeWidth={harness ? 2 : 1.25}
          className={harness ? 'k-stroke-orca k-tint-orca' : 'k-stroke-orca k-fill-none'}
        />
        <Label x={326} y={69} size={SIZE} tone="orca">
          Harness Server
        </Label>
        <Label x={326} y={90} size={SIZE}>
          runs the loop
        </Label>
      </Part>

      <Part id="consume" on={on.has('consume')}>
        <Arrow d="M276,244 V104" tip={[276, 104, 270]} on={on.has('consume')} dashed />
      </Part>

      <Part id="record" on={on.has('record')}>
        <Arrow d="M260,90 H250 V244" tip={[250, 244, 90]} on={on.has('record')} dashed />
      </Part>

      <Part id="run" on={on.has('run')}>
        <Arrow d="M340,104 V148" tip={[340, 148, 90]} on={on.has('run')} />
      </Part>

      <Part id="sandbox" on={on.has('sandbox')}>
        <Box x={292} y={148} w={96} h={44} label="Sandbox" size={SIZE} on={on.has('sandbox')} />
      </Part>

      <Part id="egress" on={on.has('egress')}>
        <Arrow d="M392,74 H446 V148" tip={[446, 148, 90]} on={on.has('egress')} />
      </Part>

      <Part id="gateway" on={on.has('gateway')}>
        <Box x={400} y={148} w={92} h={44} label="AI Gateway" size={SIZE} on={on.has('gateway')} />
      </Part>

      <Part id="to-model" on={on.has('to-model')}>
        <Arrow d="M492,164 L516,150" tip={[516, 150, -30]} on={on.has('to-model')} />
      </Part>

      <Part id="model" on={on.has('model')}>
        <Box x={516} y={126} w={116} h={40} label="Model provider" owner="you" size={SIZE} on={on.has('model')} />
      </Part>

      <Part id="to-mcp" on={on.has('to-mcp')}>
        <Arrow d="M492,176 L516,190" tip={[516, 190, 30]} on={on.has('to-mcp')} />
      </Part>

      <Part id="mcp" on={on.has('mcp')}>
        <Box x={516} y={174} w={116} h={40} label="MCP servers" owner="you" size={SIZE} on={on.has('mcp')} />
      </Part>

      <Part id="store" on={on.has('store')}>
        <Box x={140} y={244} w={352} h={40} label="Event Store" size={SIZE} on={on.has('store')} />
      </Part>

      {hops.map(n => {
        const [x, y] = HOP_AT[n];
        return (
          <g key={n} className="k-badge">
            <circle cx={x} cy={y} r={9.5} />
            <text x={x} y={y + 4} fontSize={12} textAnchor="middle">
              {n}
            </text>
          </g>
        );
      })}
    </Canvas>
  );
}

/* ── Orca 101, guide 2 ───────────────────────────────────────────────── */

type VersionsPart = 'agent' | 'v1' | 'v2' | 's1' | 's2' | 's3';
const VERSIONS_ALL = lit<VersionsPart>('agent', 'v1', 'v2', 's1', 's2', 's3');

/**
 * One agent, its versions, and the sessions pinned to them: the picture behind
 * "an agent is a definition, a session is a run". A session's arrow points at
 * the version it pinned and never moves, which is the point.
 */
export function VersionsDiagram({ name, on = VERSIONS_ALL }: { name: string; on?: ReadonlySet<VersionsPart> }) {
  const agent = on.has('agent');
  return (
    <Canvas
      width={520}
      height={210}
      label={`The agent ${name} with two versions: sessions 1 and 2 are pinned to version 1, session 3 to version 2`}
    >
      <Part id="agent" on={agent}>
        <rect
          x={16}
          y={14}
          width={488}
          height={100}
          rx={10}
          strokeWidth={agent ? 2 : 1.25}
          className="k-stroke-orca k-fill-none"
        />
        <Label x={32} y={38} anchor="start" tone="orca">
          {`Agent · ${name}`}
        </Label>
      </Part>

      <Part id="v1" on={on.has('v1')}>
        <Box x={60} y={52} w={120} h={44} label="version 1" on={on.has('v1')} />
      </Part>

      <Part id="v2" on={on.has('v2')} quiet="hide">
        <Arrow d="M180,74 H320" tip={[320, 74, 0]} on={on.has('v2')} />
        <Label x={250} y={66}>update</Label>
        <Box x={320} y={52} w={120} h={44} label="version 2" on={on.has('v2')} />
      </Part>

      <Part id="s1" on={on.has('s1')} quiet="hide">
        <Arrow d="M85,150 L100,96" tip={[100, 96, -74]} on={on.has('s1')} />
        <Box x={30} y={150} w={110} h={44} label="Session 1" on={on.has('s1')} />
      </Part>

      <Part id="s2" on={on.has('s2')} quiet="hide">
        <Arrow d="M205,150 L140,96" tip={[140, 96, -140]} on={on.has('s2')} />
        <Box x={150} y={150} w={110} h={44} label="Session 2" on={on.has('s2')} />
      </Part>

      <Part id="s3" on={on.has('s3')} quiet="hide">
        <Arrow d="M380,150 V96" tip={[380, 96, 270]} on={on.has('s3')} />
        <Box x={325} y={150} w={110} h={44} label="Session 3" on={on.has('s3')} />
      </Part>
    </Canvas>
  );
}

type TimelinePart =
  | 'app'
  | 'log'
  | 'agent'
  | 'user'
  | 'agent-events'
  | 'session-events'
  | 'span-events'
  | 'stream';
const TIMELINE_ALL = lit<TimelinePart>(
  'app',
  'log',
  'agent',
  'user',
  'agent-events',
  'session-events',
  'span-events',
  'stream',
);

/** A lifeline: where a participant's events land, top to bottom. */
function Lifeline({ x }: { x: number }) {
  return <line x1={x} y1={56} x2={x} y2={286} strokeWidth={1.25} className="k-stroke-rail" />;
}

/** A mark on the log's lifeline, for events the session writes about itself. */
function LogMark({ y, on }: { y: number; on: boolean }) {
  return (
    <rect
      x={274}
      y={y - 6}
      width={12}
      height={12}
      rx={2}
      strokeWidth={on ? 2 : 1.25}
      className={on ? 'k-fill-orca k-stroke-orca' : 'k-stroke-orca k-fill-none'}
    />
  );
}

/**
 * An application, a session's log, and the agent, as three lifelines. Your
 * application appends `user.*`, the agent appends `agent.*`, the session
 * records its own lifecycle and spans, and your application reads all of it
 * back as a stream. The stream is dashed: the agent does not wait for anyone
 * to be listening.
 */
export function TimelineDiagram({ on = TIMELINE_ALL }: { on?: ReadonlySet<TimelinePart> }) {
  return (
    <Canvas
      width={560}
      height={300}
      label="Three lifelines: your app appends user events to the session log, the agent appends agent events, the session records its own status and span events, and your app reads the log back as a stream"
    >
      <Part id="app" on={on.has('app')}>
        <Lifeline x={85} />
        <Box x={20} y={16} w={130} h={40} label="Your app" owner="you" on={on.has('app')} />
      </Part>

      <Part id="log" on={on.has('log')}>
        <Lifeline x={280} />
        <Box x={215} y={16} w={130} h={40} label="Session log" on={on.has('log')} />
      </Part>

      <Part id="agent" on={on.has('agent')}>
        <Lifeline x={475} />
        <Box x={410} y={16} w={130} h={40} label="Agent" on={on.has('agent')} />
      </Part>

      <Part id="user" on={on.has('user')}>
        <Arrow d="M85,96 H280" tip={[280, 96, 0]} on={on.has('user')} />
        <Label x={182} y={88}>user.*</Label>
      </Part>

      <Part id="agent-events" on={on.has('agent-events')}>
        <Arrow d="M475,140 H280" tip={[280, 140, 180]} on={on.has('agent-events')} />
        <Label x={378} y={132}>agent.*</Label>
      </Part>

      <Part id="session-events" on={on.has('session-events')}>
        <LogMark y={184} on={on.has('session-events')} />
        <Label x={296} y={188} anchor="start">
          session.*
        </Label>
      </Part>

      <Part id="span-events" on={on.has('span-events')}>
        <LogMark y={222} on={on.has('span-events')} />
        <Label x={296} y={226} anchor="start">
          span.*
        </Label>
      </Part>

      <Part id="stream" on={on.has('stream')}>
        <Arrow d="M280,262 H85" tip={[85, 262, 180]} on={on.has('stream')} dashed />
        <Label x={182} y={254}>stream</Label>
      </Part>
    </Canvas>
  );
}

/* ── the booth loop's example workflow ───────────────────────────────── */

function Tri({ x, y, deg, lime = false }: { x: number; y: number; deg: number; lime?: boolean }) {
  return (
    <path
      d="M0,0 L-10,-5 L-10,5 Z"
      transform={`translate(${x} ${y}) rotate(${deg})`}
      className={lime ? 'd-arrow-lime' : 'd-arrow-teal'}
    />
  );
}

function WorkflowStep({ x, n, lines }: { x: number; n: string; lines: readonly [string, string] }) {
  return (
    <>
      <rect x={x} y={105} width={230} height={120} rx={8} className="d-inner" />
      <text x={x + 115} y={140} className="d-step">
        {n}
      </text>
      <text x={x + 115} y={176} className="d-big">
        {lines[0]}
      </text>
      <text x={x + 115} y={200} className="d-big">
        {lines[1]}
      </text>
    </>
  );
}

/**
 * Live events in, governed action out: a suspicious login becomes updated risk
 * context on the data platform, Orca Agent Engine investigates, a person
 * approves the action, and the approved action lands back on the platform.
 *
 * The return path differs from the booth's drawing in two ways, both because
 * the tutorial this deck leads into writes a row to a SQL table, not an event
 * to the stream. It reads "APPROVED ACTION" where the booth's reads "RESPONSE
 * EVENTS", and it ends on the platform's box, not on step 01, the stream.
 */
export function WorkflowDiagram() {
  return (
    <svg
      viewBox="0 0 1240 372"
      role="img"
      aria-label="An example workflow: a suspicious login becomes updated risk context on the StreamNative Data Platform, Orca Agent Engine investigates, a person approves the action, and the approved action lands back on the platform"
    >
      <text x={300} y={40} className="d-step">
        STREAMNATIVE DATA PLATFORM
      </text>
      <rect x={20} y={58} width={560} height={232} rx={10} className="d-box" />

      <text x={925} y={40} className="d-step">
        ORCA AGENT ENGINE
      </text>
      <rect x={630} y={58} width={590} height={232} rx={10} className="d-engine" />

      <WorkflowStep x={40} n="01" lines={['Suspicious', 'login']} />
      <WorkflowStep x={310} n="02" lines={['Updated risk', 'context']} />
      <WorkflowStep x={650} n="03" lines={['Orca', 'investigation']} />
      <WorkflowStep x={970} n="04" lines={['Approved', 'response']} />

      <line x1={270} y1={165} x2={300} y2={165} className="d-line" />
      <Tri x={310} y={165} deg={0} />
      <line x1={540} y1={165} x2={640} y2={165} className="d-line" />
      <Tri x={650} y={165} deg={0} />

      {/* the human gate on the action */}
      <line x1={880} y1={165} x2={902} y2={165} className="d-line" />
      <Tri x={912} y={165} deg={0} />
      <path d="M925 118 V 212" className="d-lime" />
      <circle cx={925} cy={165} r={13} className="d-inner" />
      <path d="M919 165 l4.5 4.5 L932 160" className="d-lime" strokeWidth={2} />
      <line x1={938} y1={165} x2={960} y2={165} className="d-line" />
      <Tri x={970} y={165} deg={0} />
      <text x={925} y={262} className="d-step">
        APPROVAL
      </text>

      {/* the approved action lands back on the platform, between its steps */}
      <path d="M1085 225 V 330 H 290 V 300" className="d-lime" />
      <Tri x={290} y={290} deg={270} lime />
      <text x={688} y={356} className="d-step">
        APPROVED ACTION
      </text>
    </svg>
  );
}
